import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AiCompletionPurpose,
  AiItemType,
  AiSuggestionItemType,
  useRecordAiSuggestionInsertedMutation,
} from "../../autogen/bff-api";
import { useRumError } from "../errors/RumComponentContext/useRumError";
import { useStructuredCompletion } from "./useAiCompletion";
import { TipTap } from "../input/TipTap/TipTap";
import { getTiptapJsonFromMarkdown } from "../input/TipTap/markdownUtils";

export type AiMessage = { role: "SYSTEM" | "USER" | "ASSISTANT"; content: string };

export type AiDraftModalProps = {
  /** Modal header — caller decides what is being drafted (e.g. "Suggest description with AI"). */
  title: string;
  /** RUM tag for failures, e.g. "se-public-description". */
  feature: string;
  /** Domain prompt; called on each generate so it can reflect the latest field state. */
  buildMessages: () => AiMessage[];
  /** Receives the accepted draft as TipTap JSON, ready for editor.commands.setContent. */
  onInsert: (tiptapJson: string) => void;
  onClose: () => void;
  insertsInto: { itemType: AiSuggestionItemType; itemId: string };
};

const AI_ITEM_TYPE: Record<AiSuggestionItemType, AiItemType> = {
  [AiSuggestionItemType.Procurement]: AiItemType.Procurement,
  [AiSuggestionItemType.Need]: AiItemType.Need,
};

const DRAFT_PURPOSE = AiCompletionPurpose.TextDraft;

const DRAFT_SCHEMA = JSON.stringify({
  type: "object",
  additionalProperties: false,
  properties: {
    text: { type: "string", description: "The generated rich text, formatted as Markdown." },
  },
  required: ["text"],
});

export const AiDraftModal = ({ title, feature, buildMessages, onInsert, onClose, insertsInto }: AiDraftModalProps) => {
  const { t } = useTranslation();
  const runStructured = useStructuredCompletion({
    purpose: DRAFT_PURPOSE,
    feature,
    item: { type: AI_ITEM_TYPE[insertsInto.itemType], id: insertsInto.itemId },
  });
  const [recordInserted] = useRecordAiSuggestionInsertedMutation();
  const rumError = useRumError();
  const insert = (json: string) => {
    onInsert(json);
    recordInserted({ aiSuggestionInsertedRequest: { purpose: DRAFT_PURPOSE, ...insertsInto } })
      .unwrap()
      .catch((e: unknown) => rumError(e, { feature, action: "record-ai-suggestion-inserted" }));
  };

  const [draft, setDraft] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const draftJson = useMemo(() => (draft ? getTiptapJsonFromMarkdown(draft) : null), [draft]);

  const generate = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    setDraft(null);
    try {
      const json = await runStructured({ jsonSchema: DRAFT_SCHEMA, messages: buildMessages() });
      const text = parseText(json);
      if (text) setDraft(text);
      else setHasError(true);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [runStructured, buildMessages]);

  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void generate();
  }, [generate]);

  return (
    <Modal isOpen onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading && (
            <Flex align="center" gap="3" py="8" color="smMuted">
              <Spinner size="sm" />
              <Text fontSize="sm">{t("Generating a suggestion…")}</Text>
            </Flex>
          )}
          {hasError && !isLoading && (
            <Text color="smError" fontSize="sm" py="4">
              {t("Couldn't generate a suggestion. Please try again.")}
            </Text>
          )}
          {draftJson && !isLoading && <TipTap content={draftJson} editable={false} maxHeight="96" />}
        </ModalBody>
        <ModalFooter gap="2">
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="outline" colorScheme="teal" onClick={() => void generate()} isLoading={isLoading}>
            {t("Regenerate")}
          </Button>
          <Button
            colorScheme="teal"
            isDisabled={!draftJson || isLoading}
            onClick={() => draftJson && insert(draftJson)}
          >
            {t("Insert")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const parseText = (json: string): string => {
  try {
    const data = JSON.parse(json) as { text?: unknown };
    return typeof data.text === "string" ? data.text.trim() : "";
  } catch {
    return "";
  }
};
